import { BRAND_HANDLE, BRAND_SITE_URL, BRAND_SUPPORT_EMAIL } from "@/lib/brandConfig";
import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, ExternalLink, ArrowRight, Mail, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { getTranslation } from "@/lib/i18n";
import { detectCountryFromTimezone, detectCountryFromLanguage } from "@/config/countryTemplates";

type PaymentProvider = "mercadopago" | "stripe";

const DarDeBaja = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [detectedProvider, setDetectedProvider] = useState<PaymentProvider>("stripe");
  const [showStripeInstructions, setShowStripeInstructions] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useSEO({
    title: t("darDeBaja.seoTitle"),
    description: t("darDeBaja.seoDescription"),
    keywords: getTranslation(language, "common.keywords"),
    ogImage: `${BRAND_SITE_URL}/og.png`,
  });

  useEffect(() => {
    const detectUserCountry = () => {
      let country: string | null = null;

      if (typeof window !== "undefined" && (window as unknown as { __TEMPLATE_CONFIG__?: { country?: string } }).__TEMPLATE_CONFIG__) {
        country = (window as unknown as { __TEMPLATE_CONFIG__?: { country?: string } }).__TEMPLATE_CONFIG__?.country ?? null;
      }

      if (!country) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        country = detectCountryFromTimezone(timezone);
      }

      if (!country) {
        const browserLang = navigator.language || (navigator as unknown as { userLanguage?: string }).userLanguage;
        country = detectCountryFromLanguage(browserLang);
      }

      if (country === "AR") {
        setDetectedProvider("mercadopago");
      } else {
        setDetectedProvider("stripe");
      }
    };

    detectUserCountry();
  }, []);

  const handleCancelClick = () => {
    if (detectedProvider === "mercadopago") {
      window.open("https://www.mercadopago.com.ar/subscriptions", "_blank");
    } else {
      setShowStripeInstructions(true);
    }
  };

  const handleStripePortal = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch("/api/stripe/create-customer-portal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || t("darDeBaja.portalError"));
      }

      window.location.href = data.url;
    } catch (err: unknown) {
      console.error("[handleStripePortal] Error:", err);
      setError(
        err instanceof Error ? err.message : t("darDeBaja.portalConnectError")
      );
      setLoading(false);
    }
  };

  const providerInfo = useMemo(() => {
    if (detectedProvider === "mercadopago") {
      return {
        name: "MercadoPago",
        color: "bg-blue-500/10 border-blue-500/30",
        textColor: "text-blue-400",
        logo: "🇦🇷",
        description: t("darDeBaja.mpDesc"),
      };
    }
    return {
      name: "Stripe",
      color: "bg-purple-500/10 border-purple-500/30",
      textColor: "text-purple-400",
      logo: "💳",
      description: t("darDeBaja.stripeDesc"),
    };
  }, [detectedProvider, t]);

  const mpSteps = useMemo(
    () => [t("darDeBaja.mpStep1"), t("darDeBaja.mpStep2"), t("darDeBaja.mpStep3"), t("darDeBaja.mpStep4")],
    [t]
  );

  const importantItems = useMemo(
    () => [t("darDeBaja.imp1"), t("darDeBaja.imp2"), t("darDeBaja.imp3"), t("darDeBaja.imp4")],
    [t]
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <main className="w-full max-w-2xl mx-auto px-4 py-8 sm:px-6 sm:py-12 md:py-16">
        <motion.header
          className="text-center mb-8 sm:mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/5 border border-white/10 mb-4 sm:mb-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <AlertCircle className="w-7 h-7 sm:w-8 sm:h-8 text-white/80" strokeWidth={1.5} />
          </motion.div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-medium text-white tracking-tight mb-3 sm:mb-4 px-2">
            {t("darDeBaja.heading")}
          </h1>

          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <span className="w-8 sm:w-12 h-px bg-white/20" />
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-white/60 font-medium">
              {new URL(BRAND_SITE_URL).hostname}
            </p>
            <span className="w-8 sm:w-12 h-px bg-white/20" />
          </div>

          <p className="text-sm sm:text-base text-white/70 max-w-lg mx-auto px-4">{t("darDeBaja.intro")}</p>
        </motion.header>

        <motion.div
          className="w-full bg-white/5 border border-white/10 rounded-2xl shadow-2xl backdrop-blur-sm overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <div className="p-4 sm:p-6 border-b border-white/10">
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">{t("darDeBaja.manageTitle")}</h2>
            <p className="text-sm sm:text-base text-white/70">
              {t("darDeBaja.redirectToPortal", { provider: providerInfo.name })}
            </p>
          </div>

          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <motion.div
              className={`p-4 sm:p-6 rounded-xl border ${providerInfo.color}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <span className="text-4xl sm:text-5xl flex-shrink-0">{providerInfo.logo}</span>
                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold text-base sm:text-lg ${providerInfo.textColor} mb-1`}>
                    {providerInfo.name}
                  </h4>
                  <p className="text-xs sm:text-sm text-white/70 mb-2 sm:mb-3">{providerInfo.description}</p>
                  <div className="space-y-1 text-[11px] sm:text-xs text-white/60">
                    {detectedProvider === "mercadopago" ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <span className="text-green-400">✓</span>
                          <span>{t("darDeBaja.mpBullet1")}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-green-400">✓</span>
                          <span>{t("darDeBaja.mpBullet2")}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-green-400">✓</span>
                          <span>{t("darDeBaja.mpBullet3")}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5">
                          <span className="text-green-400">✓</span>
                          <span>{t("darDeBaja.stBullet1")}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-green-400">✓</span>
                          <span>{t("darDeBaja.stBullet2")}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-green-400">✓</span>
                          <span>{t("darDeBaja.stBullet3")}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            <AnimatePresence mode="wait">
              {detectedProvider === "mercadopago" ? (
                <motion.div
                  key="mercadopago"
                  className="space-y-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 sm:p-6">
                    <h4 className="font-semibold text-sm sm:text-base text-blue-400 mb-3 sm:mb-4 flex items-center gap-2">
                      <span className="text-xl sm:text-2xl">📋</span>
                      <span>{t("darDeBaja.mpStepsTitle")}</span>
                    </h4>
                    <div className="space-y-2.5 sm:space-y-3">
                      {mpSteps.map((step, index) => (
                        <motion.div
                          key={index}
                          className="flex gap-2.5 sm:gap-3 items-start"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.1 }}
                        >
                          <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs sm:text-sm font-bold">
                            {index + 1}
                          </span>
                          <span className="text-xs sm:text-sm text-white/80 pt-0.5">{step}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <motion.button
                    type="button"
                    onClick={handleCancelClick}
                    className="w-full h-12 sm:h-14 text-base sm:text-lg bg-blue-500/20 hover:bg-blue-500/30 active:bg-blue-500/40 border-2 border-blue-500/50 text-blue-400 font-semibold rounded-xl ui-transition duration-200 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="text-xl sm:text-2xl">🇦🇷</span>
                    <span>{t("darDeBaja.goMp")}</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  key="stripe"
                  className="space-y-4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {!showStripeInstructions ? (
                    <>
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 sm:p-6">
                        <h4 className="font-semibold text-sm sm:text-base text-purple-400 mb-2 sm:mb-3 flex items-center gap-2">
                          <span className="text-xl sm:text-2xl">💳</span>
                          <span>{t("darDeBaja.stripePortalTitle")}</span>
                        </h4>
                        <p className="text-xs sm:text-sm text-white/80">{t("darDeBaja.stripePortalBody")}</p>
                      </div>

                      <motion.button
                        type="button"
                        onClick={handleCancelClick}
                        className="w-full h-12 sm:h-14 text-base sm:text-lg bg-purple-500/20 hover:bg-purple-500/30 active:bg-purple-500/40 border-2 border-purple-500/50 text-purple-400 font-semibold rounded-xl ui-transition duration-200 flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="text-xl sm:text-2xl">💳</span>
                        <span>{t("darDeBaja.continueStripe")}</span>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </motion.button>
                    </>
                  ) : (
                    <motion.form
                      onSubmit={handleStripePortal}
                      className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 sm:p-6 space-y-4"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h4 className="font-semibold text-base sm:text-lg text-purple-400 mb-3 flex items-center gap-2">
                        <span className="text-xl sm:text-2xl">📧</span>
                        <span>{t("darDeBaja.emailHeading")}</span>
                      </h4>

                      <div className="space-y-3">
                        <div>
                          <label htmlFor="email" className="block text-xs sm:text-sm text-white/70 mb-2">
                            {t("darDeBaja.subscriptionEmail")}
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@email.com"
                            required
                            disabled={loading}
                            className="w-full h-11 sm:h-12 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500/50 focus:bg-white/10 ui-transition duration-200 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>

                        {error && (
                          <motion.div
                            className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs sm:text-sm text-red-400"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            {error}
                          </motion.div>
                        )}

                        <div className="bg-white/5 rounded-lg p-3 sm:p-4 border border-white/10">
                          <p className="text-[11px] sm:text-xs text-white/60 mb-2">{t("darDeBaja.stripeListIntro")}</p>
                          <div className="space-y-1 text-[11px] sm:text-xs text-white/70">
                            <div className="flex items-start gap-1.5">
                              <span className="text-purple-400">✓</span>
                              <span>{t("darDeBaja.stripeList1")}</span>
                            </div>
                            <div className="flex items-start gap-1.5">
                              <span className="text-purple-400">✓</span>
                              <span>{t("darDeBaja.stripeList2")}</span>
                            </div>
                            <div className="flex items-start gap-1.5">
                              <span className="text-purple-400">✓</span>
                              <span>{t("darDeBaja.stripeList3")}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <motion.button
                        type="submit"
                        disabled={loading || !email}
                        className="w-full h-12 sm:h-14 text-base sm:text-lg bg-purple-500/20 hover:bg-purple-500/30 active:bg-purple-500/40 border-2 border-purple-500/50 text-purple-400 font-semibold rounded-xl ui-transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                      >
                        {loading ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            <span>{t("darDeBaja.connecting")}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xl sm:text-2xl">💳</span>
                            <span>{t("darDeBaja.goStripePortal")}</span>
                            <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                          </>
                        )}
                      </motion.button>

                      <motion.button
                        type="button"
                        onClick={() => {
                          setShowStripeInstructions(false);
                          setEmail("");
                          setError("");
                        }}
                        disabled={loading}
                        className="w-full h-10 sm:h-11 text-sm sm:text-base bg-white/5 border border-white/10 text-white hover:bg-white/10 active:bg-white/15 rounded-xl ui-transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                      >
                        <span>{t("darDeBaja.back")}</span>
                      </motion.button>
                    </motion.form>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 sm:p-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
            >
              <h4 className="font-semibold text-xs sm:text-sm text-yellow-400 mb-2 flex items-center gap-1.5">
                <span>⚠️</span>
                <span>{t("darDeBaja.importantTitle")}</span>
              </h4>
              <div className="space-y-1.5 text-[11px] sm:text-xs text-white/80">
                {importantItems.map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-2"
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + index * 0.05 }}
                  >
                    <span className="text-yellow-400 flex-shrink-0">•</span>
                    <span>{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="pt-4 sm:pt-6 border-t border-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.4 }}
            >
              <h4 className="font-semibold text-sm sm:text-base mb-2 sm:mb-3 text-white">{t("darDeBaja.helpTitle")}</h4>
              <p className="text-xs sm:text-sm text-white/70 mb-3 sm:mb-4">{t("darDeBaja.helpBody")}</p>
              <div className="flex flex-col gap-2 sm:gap-3">
                <motion.button
                  type="button"
                  onClick={() => {
                    window.location.href = `mailto:${BRAND_SUPPORT_EMAIL}`;
                  }}
                  className="w-full h-11 sm:h-12 bg-white/5 border border-white/10 text-white hover:bg-white/10 active:bg-white/15 rounded-xl ui-transition duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Mail className="w-4 h-4" />
                  <span>{BRAND_SUPPORT_EMAIL}</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.section
          className="mt-8 sm:mt-12 md:mt-16 space-y-4 sm:space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
        >
          <div className="border-t border-white/10 pt-6 sm:pt-8">
            <motion.div
              className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">{t("darDeBaja.beforeTitle")}</h3>
              <div className="space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-white/80">
                <div className="flex items-start gap-2">
                  <span className="text-blue-400 flex-shrink-0 mt-0.5">•</span>
                  <span dangerouslySetInnerHTML={{ __html: t("darDeBaja.note1") }} />
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-400 flex-shrink-0 mt-0.5">•</span>
                  <span>{t("darDeBaja.note2")}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-400 flex-shrink-0 mt-0.5">•</span>
                  <span dangerouslySetInnerHTML={{ __html: t("darDeBaja.note3") }} />
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-red-400 flex-shrink-0 mt-0.5">•</span>
                  <span dangerouslySetInnerHTML={{ __html: t("darDeBaja.note4") }} />
                </div>
              </div>
            </motion.div>

            <motion.div
              className="mt-6 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <p className="text-xs sm:text-sm text-white/60 mb-3">{t("darDeBaja.termsAccept")}</p>
              <motion.button
                type="button"
                onClick={() => {
                  window.location.href = "/terminos-y-condiciones";
                }}
                className="inline-flex items-center gap-2 px-4 sm:px-6 h-10 sm:h-11 bg-white/5 border border-white/10 text-white hover:bg-white/10 active:bg-white/15 rounded-xl ui-transition duration-200 text-xs sm:text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>📄</span>
                <span>{t("darDeBaja.readTerms")}</span>
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </motion.button>
              <p className="text-[10px] sm:text-xs text-white/40 mt-3">{t("darDeBaja.termsUpdated")}</p>
            </motion.div>
          </div>
        </motion.section>
      </main>
    </div>
  );
};

export default DarDeBaja;
